import React, { useState, useEffect, useCallback } from 'react';
import { Badge } from 'react-bootstrap';
import axiosClient from '../../services/axiosClient';
import Swal from 'sweetalert2';

function getAuthHeader() {
	try {
		const str = localStorage.getItem('userDetails');
		if (!str) return {};
		const { token, tokenType } = JSON.parse(str) || {};
		if (token) return { Authorization: `${tokenType || 'Bearer'} ${token}` };
		return {};
	} catch (_) {
		return {};
	}
}

const Subscriptions = () => {
	const [subscriptions, setSubscriptions] = useState([]);
	const [filteredSubscriptions, setFilteredSubscriptions] = useState([]);
	const [loading, setLoading] = useState(true);
	const [searchTerm, setSearchTerm] = useState('');
	const [currentPage, setCurrentPage] = useState(1);
	const [perPage] = useState(15);
	const [totalPages, setTotalPages] = useState(1);
	const [statusFilter, setStatusFilter] = useState('all');
	const [sortBy] = useState('date');
	const [sortOrder] = useState('desc');
	const [actionLoading, setActionLoading] = useState(null);

	const fetchSubscriptions = useCallback(async () => {
		try {
			setLoading(true);
			const response = await axiosClient.get(
				`/admin/payments/subscriptions?page=${currentPage}&per_page=${perPage}&status=${statusFilter}&sort=${sortBy}&order=${sortOrder}`,
				{
					headers: { 'Accept': 'application/json', ...getAuthHeader() },
				}
			);
			
			// Parse la structure de l'API
			const apiData = response?.data;
			const result = apiData?.result || {};
			let list = result?.data || [];
			
			if (!Array.isArray(list)) {
				list = [];
			}
			
			setSubscriptions(list);
			setFilteredSubscriptions(list);
			setTotalPages(result?.last_page || 1);
		} catch (err) {
			console.error('Error fetching subscriptions:', err);
		} finally {
			setLoading(false);
		}
	}, [currentPage, perPage, statusFilter, sortBy, sortOrder]);

	useEffect(() => {
		fetchSubscriptions();
	}, [fetchSubscriptions]);

	useEffect(() => {
		let filtered = [...subscriptions];
		if (searchTerm) {
			filtered = filtered.filter(s =>
				s.user?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
				s.id?.toString().includes(searchTerm)
			);
		}
		setFilteredSubscriptions(filtered);
	}, [searchTerm, subscriptions]);

	const handleApprove = async (id) => {
		const result = await Swal.fire({
			title: 'Approuver l\'abonnement',
			text: 'Voulez-vous vraiment approuver cet abonnement ?',
			icon: 'question',
			showCancelButton: true,
			confirmButtonColor: '#28a745',
			cancelButtonColor: '#6c757d',
			confirmButtonText: 'Oui, approuver',
			cancelButtonText: 'Annuler',
			reverseButtons: true
		});

		if (!result.isConfirmed) return;
		
		try {
			setActionLoading(id);
			await axiosClient.put(
				`/admin/payments/${id}/approval`,
				{ active: true },
				{
					headers: { 
						'Accept': 'application/json',
						'Content-Type': 'application/json',
						...getAuthHeader() 
					},
				}
			);
			
			await fetchSubscriptions();
			
			Swal.fire({
				title: 'Approuvé !',
				text: 'L\'abonnement a été approuvé avec succès.',
				icon: 'success',
				confirmButtonColor: '#28a745',
				timer: 2000
			});
		} catch (err) {
			console.error('Error approving subscription:', err);
			const errorMsg = err.response?.data?.message || 'Erreur lors de l\'approbation de l\'abonnement';
			
			Swal.fire({
				title: 'Erreur !',
				text: errorMsg,
				icon: 'error',
				confirmButtonColor: '#dc3545'
			});
		} finally {
			setActionLoading(null);
		}
	};

	const handleReject = async (id) => {
		const result = await Swal.fire({
			title: 'Désapprouver l\'abonnement',
			text: 'Voulez-vous vraiment désapprouver cet abonnement ?',
			icon: 'warning',
			showCancelButton: true,
			confirmButtonColor: '#dc3545',
			cancelButtonColor: '#6c757d',
			confirmButtonText: 'Oui, désapprouver',
			cancelButtonText: 'Annuler',
			reverseButtons: true
		});

		if (!result.isConfirmed) return;
		
		try {
			setActionLoading(id);
			await axiosClient.put(
				`/admin/payments/${id}/approval`,
				{ active: false },
				{
					headers: { 
						'Accept': 'application/json',
						'Content-Type': 'application/json',
						...getAuthHeader() 
					},
				}
			);
			
			await fetchSubscriptions();
			
			Swal.fire({
				title: 'Désapprouvé !',
				text: 'L\'abonnement a été désapprouvé avec succès.',
				icon: 'success',
				confirmButtonColor: '#28a745',
				timer: 2000
			});
		} catch (err) {
			console.error('Error rejecting subscription:', err);
			const errorMsg = err.response?.data?.message || 'Erreur lors de la désapprobation de l\'abonnement';
			
			Swal.fire({
				title: 'Erreur !',
				text: errorMsg,
				icon: 'error',
				confirmButtonColor: '#dc3545'
			});
		} finally {
			setActionLoading(null);
		}
	};

	const getStatusBadge = (status) => {
		console.log('Status:', status);
		const statusMap = {
			'valid': { bg: 'success', text: 'Valide', icon: 'check-circle' },
			'pending': { bg: 'warning', text: 'En attente', icon: 'clock' },
			'onHold': { bg: 'info', text: 'En attente de démarrage', icon: 'pause-circle' },
			'active': { bg: 'success', text: 'Actifs/Valides', icon: 'check-circle' },
			'expired': { bg: 'secondary', text: 'Expirés', icon: 'exclamation-triangle' },
			'canceled': { bg: 'secondary', text: 'Annulés', icon: 'ban' },
			'cancelled': { bg: 'secondary', text: 'Annulés', icon: 'ban' },
			'refunded': { bg: 'dark', text: 'Remboursés', icon: 'undo' },
		};
		const config = statusMap[status] || { bg: 'secondary', text: status, icon: 'question-circle' };
		console.log('Config utilisée:', config);
		return (
			<Badge bg={config.bg} style={{ fontSize: '11px', padding: '6px 12px' }}>
				<i className={`fas fa-${config.icon} me-1`}></i>
				{config.text}
			</Badge>
		);
	};

	const getPaymentMethodBadge = (method) => {
		const methodMap = {
			'offlinepayment': { bg: 'primary', text: 'Paiement hors ligne' },
			'card': { bg: 'success', text: 'Carte bancaire' },
			'mobile': { bg: 'info', text: 'Mobile money' },
			'paypal': { bg: 'warning', text: 'PayPal' },
		};
		const config = methodMap[method] || { bg: 'secondary', text: method };
		return <Badge bg={config.bg}>{config.text}</Badge>;
	};

	const getPackageBadge = (packageName) => {
		const packageMap = {
			'Mensuel': { bg: 'primary', icon: 'calendar-alt' },
			'Standard': { bg: 'info', icon: 'star' },
			'Premium': { bg: 'warning', icon: 'crown' },
			'Essentiel': { bg: 'success', icon: 'check-circle' },
		};
		
		const config = packageMap[packageName] || { bg: 'secondary', icon: 'box' };
		return (
			<Badge bg={config.bg}>
				<i className={`fas fa-${config.icon} me-1`}></i>
				{packageName}
			</Badge>
		);
	};

	const formatDate = (dateString) => {
		if (!dateString) return '-';
		const date = new Date(dateString);
		return date.toLocaleDateString('fr-FR', { 
			year: 'numeric', 
			month: '2-digit', 
			day: '2-digit',
			hour: '2-digit',
			minute: '2-digit'
		});
	};

	const formatAmount = (amount, currency) => {
		const numAmount = parseFloat(amount || 0);
		return `${numAmount.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency || 'XOF'}`;
	};

	return (
		<>
			<style>
				{`
					.subscriptions-table {
						background: white;
						border-radius: 12px;
						overflow: hidden;
						box-shadow: 0 2px 8px rgba(0,0,0,0.08);
					}
					
					.subscriptions-table thead {
						background: linear-gradient(135deg, #1e7e34 0%, #28a745 100%);
						color: white;
					}
					
					.subscriptions-table thead th {
						font-weight: 600;
						padding: 16px 12px;
						border: none;
						font-size: 13px;
						text-transform: uppercase;
						letter-spacing: 0.5px;
					}
					
					.subscriptions-table tbody tr {
						border-bottom: 1px solid #f0f0f0;
						transition: all 0.2s ease;
					}
					
					.subscriptions-table tbody tr:hover {
						background-color: #f0fdf4;
						transform: translateX(2px);
					}
					
					.subscriptions-table tbody td {
						padding: 14px 12px;
						vertical-align: middle;
						font-size: 14px;
					}
					
					.subscription-id {
						display: inline-flex;
						align-items: center;
						justify-content: center;
						min-width: 40px;
						height: 32px;
						padding: 0 10px;
						background: linear-gradient(135deg, #1e7e34 0%, #28a745 100%);
						color: white;
						border-radius: 20px;
						font-weight: 600;
						font-size: 13px;
					}
					
					.user-info {
						display: flex;
						align-items: center;
						gap: 10px;
					}
					
					.user-avatar {
						width: 36px;
						height: 36px;
						border-radius: 50%;
						background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
						display: flex;
						align-items: center;
						justify-content: center;
						color: white;
						font-weight: 600;
						font-size: 14px;
					}
					
					.user-details {
						display: flex;
						flex-direction: column;
					}
					
					.user-name {
						font-weight: 500;
						color: #1f2937;
						font-size: 14px;
					}
					
					.user-email {
						font-size: 12px;
						color: #9ca3af;
					}
					
					.filter-btn {
						border: 2px solid #e5e7eb;
						border-radius: 8px;
						padding: 8px 16px;
						background: white;
						color: #374151;
						font-weight: 500;
						transition: all 0.2s;
						cursor: pointer;
					}
					
					.filter-btn:hover {
						border-color: #1e7e34;
						color: #1e7e34;
					}
					
					.filter-btn.active {
						background: linear-gradient(135deg, #1e7e34 0%, #28a745 100%);
						color: white;
						border-color: transparent;
					}
					
					.pagination-modern {
						display: flex;
						gap: 8px;
						align-items: center;
					}
					
					.pagination-modern button {
						border: 2px solid #e5e7eb;
						border-radius: 8px;
						padding: 8px 14px;
						background: white;
						color: #374151;
						font-weight: 500;
						transition: all 0.2s;
						cursor: pointer;
					}
					
					.pagination-modern button:hover:not(:disabled) {
						border-color: #1e7e34;
						color: #1e7e34;
					}
					
					.pagination-modern button.active {
						background: linear-gradient(135deg, #1e7e34 0%, #28a745 100%);
						color: white;
						border-color: transparent;
					}
					
					.pagination-modern button:disabled {
						opacity: 0.5;
						cursor: not-allowed;
					}
					
					.action-btn {
						padding: 6px 12px;
						border-radius: 6px;
						border: none;
						font-size: 13px;
						font-weight: 500;
						cursor: pointer;
						transition: all 0.2s;
						margin: 0 2px;
					}
					
					.action-btn-view {
						background: #e0f2fe;
						color: #0369a1;
					}
					
					.action-btn-view:hover {
						background: #0369a1;
						color: white;
					}
					
					.action-btn-approve {
						background: #dcfce7;
						color: #166534;
					}
					
					.action-btn-approve:hover {
						background: #166534;
						color: white;
					}
					
					.action-btn-reject {
						background: #fee2e2;
						color: #991b1b;
					}
					
					.action-btn-reject:hover {
						background: #991b1b;
						color: white;
					}
					
					.stats-card {
						background: white;
						border-radius: 12px;
						padding: 20px;
						box-shadow: 0 2px 8px rgba(0,0,0,0.08);
						border-left: 4px solid #1e7e34;
					}
					
					.stats-value {
						font-size: 28px;
						font-weight: 700;
						color: #1e7e34;
					}
					
					.stats-label {
						font-size: 13px;
						color: #6b7280;
						text-transform: uppercase;
						letter-spacing: 0.5px;
					}
				`}
			</style>

			{/* Header */}
			<div className="row mb-4">
				<div className="col-12">
					<div className="d-flex justify-content-between align-items-center">
						<div>
							<h2 style={{ fontSize: '28px', fontWeight: '700', color: '#1f2937' }}>
								<i className="fas fa-credit-card me-3" style={{ color: '#1e7e34' }}></i>
								Abonnements
							</h2>
							<p className="text-muted">Gestion des abonnements et paiements</p>
						</div>
					</div>
				</div>
			</div>

			{/* Filters */}
			<div className="row mb-4">
				<div className="col-md-8">
					<div className="d-flex gap-2 flex-wrap">
						<button 
							className={`filter-btn ${statusFilter === 'all' ? 'active' : ''}`}
							onClick={() => setStatusFilter('all')}
						>
							Tous
						</button>
						<button 
							className={`filter-btn ${statusFilter === 'valid' ? 'active' : ''}`}
							onClick={() => setStatusFilter('valid')}
						>
							Valides
						</button>
						<button 
							className={`filter-btn ${statusFilter === 'pending' ? 'active' : ''}`}
							onClick={() => setStatusFilter('pending')}
						>
							En attente
						</button>
						<button 
							className={`filter-btn ${statusFilter === 'expired' ? 'active' : ''}`}
							onClick={() => setStatusFilter('expired')}
						>
							Expirés
						</button>
						<button 
							className={`filter-btn ${statusFilter === 'onHold' ? 'active' : ''}`}
							onClick={() => setStatusFilter('onHold')}
						>
							En attente de démarrage
						</button>
					</div>
				</div>
				<div className="col-md-4">
					<div className="input-group">
						<span className="input-group-text bg-white">
							<i className="fas fa-search text-muted"></i>
						</span>
						<input
							type="text"
							className="form-control"
							placeholder="Rechercher par ID ou utilisateur..."
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
						/>
					</div>
				</div>
			</div>

			{/* Table */}
			<div className="row">
				{loading ? (
					<div className="col-12 text-center py-5">
						<div className="spinner-border text-success" role="status">
							<span className="visually-hidden">Chargement...</span>
						</div>
					</div>
				) : (
					<table className="subscriptions-table w-100">
						<thead>
							<tr>
								<th>ID</th>
								<th>Date</th>
								<th>Utilisateur</th>
								<th>Package</th>
								<th>Moyen de paiement</th>
								<th>Montant</th>
								<th>Statut</th>
								<th>Actions</th>
							</tr>
						</thead>
						<tbody>
							{!filteredSubscriptions || filteredSubscriptions.length === 0 ? (
								<tr>
									<td colSpan="8" className="text-center py-4">
										<p className="text-muted">Aucun abonnement trouvé</p>
									</td>
								</tr>
							) : (
								filteredSubscriptions.map((sub) => (
									<tr key={sub.id}>
										<td>
											<span className="subscription-id">{sub.id}</span>
										</td>
										<td style={{ fontSize: '13px', color: '#6b7280' }}>
											{formatDate(sub.date)}
										</td>
										<td>
											<div className="user-info">
												<div className="user-avatar">
													{sub.user?.name?.charAt(0).toUpperCase() || 'U'}
												</div>
												<div className="user-details">
													<span className="user-name">{sub.user?.name || 'Utilisateur'}</span>
													<span className="user-email">{sub.user?.email || '-'}</span>
												</div>
											</div>
										</td>
										<td>
											{getPackageBadge(sub.package?.name || 'Standard')}
											<div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>
												{sub.package?.type || '-'}
											</div>
										</td>
										<td>
											{getPaymentMethodBadge(sub.payment_method)}
										</td>
										<td style={{ fontWeight: '600', color: '#1e7e34' }}>
											{formatAmount(sub.amount, sub.currency_code)}
										</td>
										<td className="text-center">
											<span key={`status-${sub.id}-${sub.status}`}>
												{getStatusBadge(sub.status)}
											</span>
										</td>
										<td>
											<div className="d-flex gap-1">
												<button 
													className="action-btn action-btn-view"
													title="Voir détails"
													onClick={() => window.location.href = `#/subscription/${sub.id}`}
													disabled={actionLoading === sub.id}
												>
													<i className="fas fa-eye"></i>
												</button>
												
												{!sub.approved && (
													<button 
														className="action-btn action-btn-approve"
														title="Approuver"
														onClick={() => handleApprove(sub.id)}
														disabled={actionLoading === sub.id}
													>
														{actionLoading === sub.id ? (
															<span className="spinner-border spinner-border-sm"></span>
														) : (
															<i className="fas fa-check"></i>
														)}
													</button>
												)}
												
												{sub.approved && (
													<button 
														className="action-btn action-btn-reject"
														title="Désapprouver"
														onClick={() => handleReject(sub.id)}
														disabled={actionLoading === sub.id}
													>
														{actionLoading === sub.id ? (
															<span className="spinner-border spinner-border-sm"></span>
														) : (
															<i className="fas fa-times"></i>
														)}
													</button>
												)}
											</div>
										</td>
									</tr>
								))
							)}
						</tbody>
					</table>
				)}
			</div>

			{/* Pagination */}
			{!loading && filteredSubscriptions.length > 0 && (
				<div className="row mt-4">
					<div className="col-12">
						<div className="d-flex justify-content-between align-items-center">
							<p className="text-muted mb-0">
								Page {currentPage} sur {totalPages}
							</p>
							<div className="pagination-modern">
								<button 
									onClick={() => setCurrentPage(1)}
									disabled={currentPage === 1}
								>
									<i className="fas fa-angle-double-left"></i>
								</button>
								<button 
									onClick={() => setCurrentPage(currentPage - 1)}
									disabled={currentPage === 1}
								>
									<i className="fas fa-angle-left"></i>
								</button>
								
								{[...Array(Math.min(5, totalPages))].map((_, i) => {
									let pageNum;
									if (totalPages <= 5) {
										pageNum = i + 1;
									} else if (currentPage <= 3) {
										pageNum = i + 1;
									} else if (currentPage >= totalPages - 2) {
										pageNum = totalPages - 4 + i;
									} else {
										pageNum = currentPage - 2 + i;
									}
									
									return (
										<button 
											key={i}
											onClick={() => setCurrentPage(pageNum)}
											className={currentPage === pageNum ? 'active' : ''}
										>
											{pageNum}
										</button>
									);
								})}
								
								<button 
									onClick={() => setCurrentPage(currentPage + 1)}
									disabled={currentPage === totalPages}
								>
									<i className="fas fa-angle-right"></i>
								</button>
								<button 
									onClick={() => setCurrentPage(totalPages)}
									disabled={currentPage === totalPages}
								>
									<i className="fas fa-angle-double-right"></i>
								</button>
							</div>
						</div>
					</div>
				</div>
			)}
		</>
	);
};

export default Subscriptions;
